import { useConvexAction, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import { useMutation } from '@tanstack/react-query'
import { useCallback, useRef } from 'react'

const MIN_LUMINANCE_THRESHOLD = 15
const MAX_SEEK_ATTEMPTS = 3
const SEEK_STEP_SEC = 5
const THUMBNAIL_WIDTH_PX = 640
const THUMBNAIL_HEIGHT_PX = 360
const THUMBNAIL_QUALITY = 0.8

const measureAverageLuminance = (canvas: HTMLCanvasElement): number => {
  const ctx = canvas.getContext('2d')
  if (!ctx) return 0

  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height)
  let luminanceSum = 0
  const pixelCount = data.length / 4

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i] ?? 0
    const g = data[i + 1] ?? 0
    const b = data[i + 2] ?? 0
    luminanceSum = luminanceSum + 0.299 * r + 0.587 * g + 0.114 * b
  }

  return luminanceSum / pixelCount
}

type UseThumbnailExtractorArgs = {
  readonly interviewId: Id<'interviews'>
  readonly recordingUrl: string
  readonly durationSec: number
  readonly onThumbnailSaved: (thumbnailR2Key: string) => void
}

export const useThumbnailExtractor = ({
  interviewId,
  recordingUrl,
  durationSec,
  onThumbnailSaved,
}: UseThumbnailExtractorArgs) => {
  const hiddenVideoRef = useRef<HTMLVideoElement | null>(null)
  const seekAttemptsRef = useRef(0)
  const seekPositionRef = useRef(Math.max(durationSec * 0.1, 5))

  const { mutateAsync: generateUploadUrl } = useMutation({
    mutationFn: useConvexAction(
      api.features.interviews.interview_thumbnail.generateMyThumbnailUploadUrl,
    ),
  })

  const { mutateAsync: saveThumbnail } = useMutation({
    mutationFn: useConvexMutation(
      api.features.interviews.interview_thumbnail.patchMyInterviewThumbnail,
    ),
  })

  const extractAndUploadThumbnail = useCallback(async (): Promise<void> => {
    const video = hiddenVideoRef.current
    if (!video) return

    const canvas = document.createElement('canvas')
    canvas.width = THUMBNAIL_WIDTH_PX
    canvas.height = THUMBNAIL_HEIGHT_PX
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.drawImage(video, 0, 0, THUMBNAIL_WIDTH_PX, THUMBNAIL_HEIGHT_PX)
    const luminance = measureAverageLuminance(canvas)

    if (
      luminance < MIN_LUMINANCE_THRESHOLD &&
      seekAttemptsRef.current < MAX_SEEK_ATTEMPTS
    ) {
      seekAttemptsRef.current += 1
      seekPositionRef.current += SEEK_STEP_SEC
      video.currentTime = seekPositionRef.current
      return
    }

    canvas.toBlob(
      async (blob) => {
        if (!blob) return

        const result = await generateUploadUrl({ interviewId })
        if (!result.ok) return

        await fetch(result.uploadUrl, {
          method: 'PUT',
          body: blob,
          headers: { 'Content-Type': 'image/jpeg' },
        })

        await saveThumbnail({
          interviewId,
          thumbnailR2Key: result.r2Key,
        })

        onThumbnailSaved(result.r2Key)
      },
      'image/jpeg',
      THUMBNAIL_QUALITY,
    )
  }, [interviewId, generateUploadUrl, saveThumbnail, onThumbnailSaved])

  const startExtraction = useCallback((): void => {
    const video = document.createElement('video')
    video.crossOrigin = 'anonymous'
    video.muted = true
    video.src = recordingUrl
    hiddenVideoRef.current = video

    video.addEventListener('seeked', () => {
      extractAndUploadThumbnail()
    })

    video.addEventListener('loadedmetadata', () => {
      video.currentTime = seekPositionRef.current
    })
  }, [recordingUrl, extractAndUploadThumbnail])

  return { startExtraction }
}
